import * as React from "react";
import { SimpleContainerModel, SimpleContainerProps } from "./typings";
import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "bernie-view";
import {
  BlossomComponentFactory,
  BlossomComponentFactoryProps,
} from "components/flexFramework/domain/BlossomComponentFactory";
import { BlossomComponents } from "components/flexFramework/BlossomComponents/BlossomComponents";

const DEFAULT_WIDTH = "100";

export const SimpleContainer = (props: SimpleContainerProps) => {
  const blossomComponent = BlossomComponentFactory.get(props as BlossomComponentFactoryProps);
  const context = props.context;

  if (!blossomComponent) {
    return null;
  }

  const model = blossomComponent.getModel<SimpleContainerModel>();
  const { name, mobileWidth, title, width, innerTitle, tabletWidth, desktopWidth } = model;
  const modules = blossomComponent.getBlossomComponents();

  // Return null for regions with no title, inner title and all "empty" modules
  if (
    title &&
    innerTitle &&
    modules &&
    modules.length > 0 &&
    modules.filter((module) => module.shouldRender()).length === 0
  ) {
    return null;
  }

  const headingClass = "uitk-type-display-700 all-t-padding-three uitk-cell all-cell-1-1 all-l-padding-zero";
  const regionSpecificClasses = getRegionSpecificClasses(blossomComponent.name);
  const gridWidth = regionWidthToGridMap[width || DEFAULT_WIDTH];
  const gridTabletWidth = regionWidthToGridMap[tabletWidth || DEFAULT_WIDTH];
  const gridMobileWidth = regionWidthToGridMap[mobileWidth || DEFAULT_WIDTH];
  const flexContent = (
    <>
      {title && <h2 className={headingClass}>{title}</h2>}
      {innerTitle && <h2 className={headingClass}>{innerTitle}</h2>}
      <BlossomComponents blossomComponent={blossomComponent} context={context} />
    </>
  );

  const commonClassNames = ["uitk-grid uitk-cell", regionSpecificClasses, getExtraClassNameBasedOnParameters(model)]
    .filter((classes) => classes !== "")
    .join(" ");

  return (
    <Viewport>
      {mobileWidth === "hidden" ? (
        <ViewSmall />
      ) : (
        <ViewSmall>
          <div
            className={`SimpleContainer ${
              name === "dx-page-header" ? " elevation-4" : ""
            } ${commonClassNames} ${gridMobileWidth} `}
            role="region"
          >
            {flexContent}
          </div>
        </ViewSmall>
      )}

      {tabletWidth === "hidden" ? (
        <ViewMedium />
      ) : (
        <ViewMedium>
          <div
            className={`SimpleContainer ${
              name === "dx-page-header" ? " elevation-4" : ""
            } ${commonClassNames} ${gridTabletWidth} `}
            role="region"
          >
            {flexContent}
          </div>
        </ViewMedium>
      )}

      {desktopWidth === "hidden" ? (
        <ViewLarge />
      ) : (
        <ViewLarge>
          <div
            className={`${commonClassNames} ${gridWidth} ${
              name === "dx-page-header" ? "elevation-4" : " SimpleContainer"
            }`}
            role="region"
          >
            {flexContent}
          </div>
        </ViewLarge>
      )}
    </Viewport>
  );
};

/**
 * Return the classes corresponding to a specific region. This is
 * a temporary solution until Module groups are implemented
 * @param {string} regionName - Name of region
 * @return {string} classes to decorate region
 */
/* istanbul ignore next */
const getRegionSpecificClasses = (regionName: string): string => {
  switch (regionName) {
    case "wizard":
      return "wizardHero all-b-margin-three whiteBackground";
    case "FullWidthWizard":
      return "wizardHero fullWidth";
    case "FullWidthBreadcrumbs":
      return "fullWidthBreadcrumbs";
    case "CallToActionGroup":
      return "callToActionGroup";
    case "all-b-padding-three":
      return "all-b-padding-three";
    case "StorefrontConfidence":
      return "storefrontConfidence";
    case "all-cell-1-3":
      return "subRegion all-cell-1-3";
    case "dx-sidebar-content":
      return "dx-sidebar-content";
    case "SEO links":
      return "SEOLinks";
    case "CenteredDestinationWizard":
      return "CenteredDestinationWizard";
    case "FullWidthConfidenceMessages":
      return "confidenceMessages fullWidth";
    case "RecentlyBookedReviewedNoBorderBkg":
      return "recentlyBookedReviewed noBorder noBackground";
    case "NativeShowcase":
      return "all-y-padding-three";
    case "Weekend Getaways":
      return "all-t-padding-three";
    case "PropertyHeroImage":
      return "m-l-padding-three l-l-padding-three";
    case "PropertyReviewHeadline":
      return "m-r-padding-three l-r-padding-three";
  }

  return "";
};

const regionWidthToGridMap = {
  "25": "all-cell-1-4",
  "33": "all-cell-1-3",
  "50": "all-cell-1-2",
  "66": "all-cell-2-3",
  "75": "all-cell-3-4",
  "100": "all-cell-1-1",
};

const buildClassNameToParameterConditionMap = (model: SimpleContainerModel) => ({
  fullScreenWidth: model.fullWidth,
  whiteBackground: model.backgroundColor === "white",
  "region-bottom-border": model.border === "bottom",
  "l-x-padding-three xl-x-padding-three all-y-padding-six": model.name === "dx-main-content",
  "all-b-padding-six": model.name === "dx-sidebar-content",
  dxPageContent: model.name === "dx-page-content",
  "region-without-border": model.border && model.border.includes("none"),
  "bex-homepage-module": model.brandModulePadding && model.brandModulePadding === "bex_padding",
  "comet-homepage-module": model.brandModulePadding && model.brandModulePadding === "comet_padding",
});

const getExtraClassNameBasedOnParameters = (model: SimpleContainerModel) => {
  const classMap = buildClassNameToParameterConditionMap(model);

  return Object.keys(classMap)
    .filter((x) => classMap[x])
    .join(" ");
};

export default SimpleContainer;
